import styled from "styled-components";

// Shared at-a-glance summary block for the metric detail pages (extracted from
// TempartureDetail so OperationDetail/PowerDetail/IotDetail render the same
// PDF-mirroring chrome): section head, first/latest side boxes flanking the
// stat tiles, the report-type/recording-period line, and the status banner.
// Pages compose the inner markup with the `sum__*` classes.
export const SummaryBlock = styled.div`
  /* thin rule between the device headline row above and this summary section —
     the same section separation rhythm as the PDF */
  margin: 16px 0 0;
  padding: 14px 0 0;
  border-top: 1px solid #e2e8f0;
  .sum {
    /* PDF-mirroring layout: section head, then first-box | tiles | latest-box */
    &__head {
      font-size: 15px;
      font-weight: 700;
      color: #1f2328;
      margin: 0 0 10px;
    }
    /* no outer box — thin internal rules only, matching the PDF's airy look */
    &__row {
      display: flex;
      align-items: stretch;
    }
    &__side {
      flex: 0 0 170px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: 14px 10px;
      &--first {
        border-right: 1px solid #e2e8f0;
      }
      &--last {
        border-left: 1px solid #e2e8f0;
      }
    }
    &__center {
      flex: 1;
      display: flex;
      flex-direction: column;
    }
    /* muted navy accents, same quiet highlight as the PDF badges */
    &__side-badge {
      font-size: 12px;
      font-weight: 700;
      color: #2b3a5c;
      background: #eef2f7;
      padding: 3px 10px;
      border-radius: 6px;
      white-space: nowrap;
    }
    &__side-val {
      font-size: 22px;
      font-weight: 700;
      color: #1f2a44;
      margin: 8px 0 0;
    }
    &__side-cap {
      font-size: 11px;
      color: #64748b;
      margin: 4px 0 0;
    }
    &__tiles {
      flex: 1;
      display: flex;
    }
    /* narrow screens: side boxes become full-width rows above/below the tiles */
    @media (max-width: 900px) {
      &__row {
        flex-wrap: wrap;
      }
      &__side {
        flex: 1 1 100%;
        &--first {
          border-right: none;
          border-bottom: 1px solid #e2e8f0;
        }
        &--last {
          border-left: none;
          border-top: 1px solid #e2e8f0;
        }
      }
      &__center {
        flex: 1 1 100%;
      }
    }
    &__tile {
      flex: 1;
      text-align: center;
      padding: 14px 8px;
      border-left: 1px solid #eef1f4;
      &:first-child {
        border-left: none;
      }
    }
    &__tile-label {
      font-size: 12px;
      color: #64748b;
    }
    &__tile-value {
      font-size: 22px;
      font-weight: 700;
      margin: 6px 0 0;
    }
    /* sits under the tiles inside the center column, split from them by a thin
       rule spanning only that column — same as the PDF's duration row */
    &__duration {
      text-align: center;
      font-size: 12px;
      color: #64748b;
      margin: 4px 16px 0;
      padding: 10px 0 12px;
      border-top: 1px solid #e2e8f0;
    }
    &__rows {
      text-align: center;
      font-size: 11px;
      color: #94a3b8;
      margin: 4px 0 0;
    }
    /* slim, flat band like the PDF banner — quiet unless something is wrong */
    &__banner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      margin: 14px 0 0;
      padding: 9px 14px;
      border-radius: 8px;
      border: 1px solid;
      flex-wrap: wrap;
      &--breach {
        background: #fef2f2;
        border-color: #fecaca;
      }
      /* every excursion returned in-range within the device's alert delay —
         noteworthy but not an alarm, so soft amber instead of red */
      &--brief {
        background: #fffbeb;
        border-color: #fde68a;
      }
      &--ok {
        background: #f0fdf4;
        border-color: #bbf7d0;
      }
    }
    &__banner-chips {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }
    &__banner-title {
      font-size: 13px;
      font-weight: 700;
      color: #1f2328;
    }
    &__banner-range {
      font-size: 11.5px;
      color: #64748b;
      margin: 2px 0 0;
    }
    &__banner-count {
      font-size: 12px;
      font-weight: 700;
      padding: 4px 10px;
      border-radius: 6px;
      white-space: nowrap;
    }
    &__banner--breach &__banner-count {
      background: #fee2e2;
      color: #b91c1c;
    }
    &__banner--ok &__banner-count {
      background: #dcfce7;
      color: #16a34a;
    }
    /* the brief-excursion chip keeps its amber look in every banner state */
    &__banner-count--brief {
      background: #fef3c7 !important;
      color: #b45309 !important;
    }
  }
`;
